/**
 * Copy text to the clipboard; resolves to true if it worked.
 *
 * The Clipboard API exists only in secure contexts (HTTPS or localhost), so on
 * e.g. http://192.168.x.x it is undefined. Then (or if it is refused) the text
 * is copied through a hidden textarea and document.execCommand('copy'), which
 * must run synchronously inside the click - hence no await before it.
 */
export async function copyText(text) {
  if (window.isSecureContext && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      /* fall back below */
    }
  }
  // Inside a modal dialog the focus is trapped: put the textarea in the dialog
  const host = document.activeElement?.closest?.('[role="dialog"]') || document.body;
  const area = document.createElement('textarea');
  area.value = text;
  area.setAttribute('readonly', '');
  area.style.position = 'fixed';
  area.style.top = '-1000px';
  area.style.opacity = '0';
  host.appendChild(area);
  area.focus();
  area.select();
  area.setSelectionRange(0, text.length);
  let ok = false;
  try {
    ok = document.execCommand('copy');
  } catch {
    ok = false;
  }
  host.removeChild(area);
  return ok;
}

/**
 * Copy text that is still being fetched; resolves to true if it worked.
 *
 * Safari only allows clipboard writes during the click itself, so the write
 * starts right away with a ClipboardItem whose content is the pending text.
 * Where that is not supported, the text is copied once it arrives (fine in
 * Chrome; Safari may refuse, and the caller can offer a second tap).
 */
export async function copyPending(textPromise) {
  if (window.isSecureContext && window.ClipboardItem && navigator.clipboard?.write) {
    try {
      const blob = textPromise.then((text) => new Blob([text], { type: 'text/plain' }));
      await navigator.clipboard.write([new window.ClipboardItem({ 'text/plain': blob })]);
      return true;
    } catch {
      /* fall back below */
    }
  }
  try {
    return await copyText(await textPromise);
  } catch {
    return false;
  }
}
